import axios from 'axios';
import type { GenerateParams, GeneratedImage, ShareLink, Project, ProjectImage, ImageVersion, Conversation, ChatMessage } from '@/types';

const api = axios.create({
  baseURL: '/api',
  timeout: 300000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('pmsupers_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
}

function convertKeys(obj: any): any {
  if (Array.isArray(obj)) return obj.map(convertKeys);
  if (obj !== null && typeof obj === 'object' && !(obj instanceof Date)) {
    const result: any = {};
    for (const key of Object.keys(obj)) {
      result[snakeToCamel(key)] = convertKeys(obj[key]);
    }
    return result;
  }
  return obj;
}

api.interceptors.response.use(
  (response) => {
    if (response.data && typeof response.data === 'object') {
      response.data = convertKeys(response.data);
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('pmsupers_token');
    }
    const serverError = error.response?.data?.error || '';
    const serverDetail = error.response?.data?.message || '';
    const serverStack = error.response?.data?.stack?.join('\n') || '';
    const parts = [serverError, serverDetail, serverStack].filter(Boolean);
    const message = parts.length > 0 ? parts.join(': ') : (error.message || '请求失败');
    error.userMessage = message;
    if (serverStack) {
      console.error('Server stack trace:', error.response?.data?.stack);
    }
    return Promise.reject(error);
  }
);

export const imageApi = {
  generate: (params: GenerateParams) =>
    api.post<{ images: GeneratedImage[]; took: number }>('/images/generate', params),

  edit: (imageId: string, prompt: string, mask?: File) => {
    const formData = new FormData();
    formData.append('imageId', imageId);
    formData.append('prompt', prompt);
    if (mask) formData.append('mask', mask);
    return api.post<{ images: GeneratedImage[] }>('/images/edit', formData);
  },

  upscale: (imageId: string, scale: number) =>
    api.post<{ image: GeneratedImage }>(`/images/upscale`, { imageId, scale }),

  batchGenerate: (paramsList: GenerateParams[]) =>
    api.post<{ results: Array<{ images: GeneratedImage[] }> }>('/images/batch', { paramsList }),

  generatePrompt: (params: { description: string; scene?: string; industry?: string; style?: string; refImage?: string; docContent?: string }) =>
    api.post<{ prompt: string }>('/images/prompt/generate', params),

  optimizePrompt: (params: { prompt: string; target?: string }) =>
    api.post<{ prompt: string }>('/images/prompt/optimize', params),

  analyze: (params: { imageUrl?: string; content?: string; generateSuggestions?: boolean; originalPrompt?: string; sceneId?: string; optimizeTarget?: string }) =>
    api.post<{ prompt: string; suggestions?: string[] }>('/images/analyze', params),
};

export const templateApi = {
  getAll: (filters?: Record<string, string[]>) =>
    api.get('/templates', { params: filters }),

  getById: (id: string) =>
    api.get(`/templates/${id}`),

  getCategories: () =>
    api.get('/templates/categories'),

  saveCustom: (template: Record<string, unknown>) =>
    api.post('/templates/custom', template),

  getCustom: () =>
    api.get('/templates/custom'),

  deleteCustom: (id: string) =>
    api.delete(`/templates/custom/${id}`),
};

export const historyApi = {
  getAll: (page: number, pageSize: number) =>
    api.get<{ items: any[]; total: number }>('/history', { params: { page, pageSize } }),

  getById: (id: string) =>
    api.get<any>(`/history/${id}`),

  delete: (id: string) =>
    api.delete(`/history/${id}`),

  favorite: (imageId: string) =>
    api.post(`/history/favorite/${imageId}`),

  unfavorite: (imageId: string) =>
    api.delete(`/history/favorite/${imageId}`),

  getFavorites: (page: number, pageSize: number) =>
    api.get<{ items: GeneratedImage[]; total: number }>('/history/favorites', { params: { page, pageSize } }),
};

export const shareApi = {
  create: (historyId: string) =>
    api.post<ShareLink>('/share', { historyId }),

  get: (shareId: string) =>
    api.get<ShareLink>(`/share/${shareId}`),
};

// 用户反馈 API
export const userFeedbackApi = {
  // 提交反馈
  submit: (data: FormData) =>
    api.post<{ success: boolean; message: string; feedbackId: number }>('/feedback', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  // 获取我的反馈列表
  getMyFeedbacks: (page?: number, pageSize?: number) =>
    api.get<{ success: boolean; data: { list: any[]; pagination: { page: number; pageSize: number; total: number; totalPages: number } } }>('/feedback/my', {
      params: { page, pageSize },
    }),

  // 获取反馈详情
  getFeedbackDetail: (id: number) =>
    api.get<any>(`/feedback/${id}`),

  // 管理后台：获取所有反馈
  getAdminFeedbacks: (params?: { page?: number; pageSize?: number; status?: string; feedbackType?: string }) =>
    api.get<{ list: any[]; pagination: { page: number; pageSize: number; total: number; totalPages: number } }>('/feedback/admin/list', { params }),

  // 管理后台：审核反馈
  reviewFeedback: (id: number, data: { status: 'approved' | 'rejected'; adminReply?: string; rewardCredits?: number }) =>
    api.post<{ success: boolean; message: string; rewardCredits: number }>(`/feedback/admin/${id}/review`, data),

  // 管理后台：获取反馈详情
  getAdminFeedbackDetail: (id: number) =>
    api.get<any>(`/feedback/admin/${id}`),
};

export const authApi = {
  register: (data: { username: string; email: string; password: string; displayName?: string; inviteCode: string; verificationCode: string }) =>
    api.post<{ token: string; user: any }>('/auth/register', data),

  login: (data: { username: string; password: string }) =>
    api.post<{ token: string; user: any }>('/auth/login', data),

  me: () =>
    api.get<any>('/auth/me'),

  updateProfile: (data: Record<string, any>) =>
    api.put('/auth/profile', data),

  changePassword: (data: { currentPassword: string; newPassword: string; verifyCode: string }) =>
    api.put<{ success: boolean; message: string }>('/auth/password', data),

  deleteAccount: (data: { verifyCode: string; password: string }) =>
    api.delete<{ success: boolean; message: string }>('/auth/account', { data }),

  uploadAvatar: (data: { imageBase64: string }) =>
    api.post<{ url: string; filePath: string }>('/auth/upload-avatar', data),

  wpLogin: (data: { wp_token: string; wp_password: string; invite_code?: string }) =>
    api.post<{ token?: string; user?: any; hasLocalPassword?: boolean; requiresPassword?: boolean; wpUser?: any; code?: string; existingUserId?: number; existingUsername?: string }>('/auth/wp-login', data),

  wpBindEmail: (data: { email: string; existing_user_id: number; verification_code: string; wp_token: string }) =>
    api.post<{ token: string; user: any }>('/auth/wp-bind-email', data),

  wpSendBindCode: (data: { wp_token: string }) =>
    api.post<{ success: boolean; message: string }>('/auth/wp-send-bind-code', data),

  wpSetUsername: (data: { wp_token: string; wp_password: string; new_username: string; invite_code?: string }) =>
    api.post<{ token: string; user: any }>('/auth/wp-set-username', data),

  wpRegister: (data: { wp_token: string; password: string; email?: string }) =>
    api.post<{ token: string; user: any }>('/auth/wp-register', data),

  bindWP: (data: { username: string; password: string }) =>
    api.post<{ success: boolean; message: string; data: any & { benefits?: { verificationReward: number; discountPercent: number } } }>('/auth/bind-wp', data),

  unbindWp: () =>
    api.post<{ success: boolean; message: string; data?: { revokedBenefits?: string[] } }>('/auth/unbind-wp'),

  syncWP: () =>
    api.post<{ success: boolean; message: string; data: any }>('/auth/sync-wp'),

  setLocalPassword: (data: { password: string; currentPassword?: string }) =>
    api.put<{ success: boolean; message: string }>('/auth/set-local-password', data),

  forgotPassword: (data: { email: string }) =>
    api.post<{ success: boolean; message: string }>('/auth/forgot-password', data),

  resetPassword: (data: { token: string; newPassword: string }) =>
    api.post<{ success: boolean; message: string }>('/auth/reset-password', data),

  sendVerificationCode: (data: { email: string; type: 'register' | 'reset_password' | 'delete_account' | 'wp_bind' }) =>
    api.post<{ success: boolean; message: string }>('/auth/send-verification-code', data),

  resetPasswordByCode: (data: { email: string; code: string; password: string }) =>
    api.post<{ success: boolean; message: string }>('/auth/reset-password-by-code', data),

  verifyRegisterCode: (data: { email: string; code: string }) =>
    api.post<{ success: boolean; message: string }>('/auth/verify-register-code', data),

  getIdentities: () =>
    api.get<{ identities: { id: string; name: string; icon: string | null; color: string; priority: number }[] }>('/auth/identities'),

  grantIdentity: (userId: number, identity: string) =>
    api.post<{ success: boolean; message: string; identities: string[] }>(`/auth/admin/users/${userId}/grant-identity`, { identity }),

  revokeIdentity: (userId: number, identity: string) =>
    api.post<{ success: boolean; message: string; identities: string[] }>(`/auth/admin/users/${userId}/revoke-identity`, { identity }),
};

export const industriesApi = {
  getAll: () =>
    api.get<{ success: boolean; data: { id: number; name: string }[] }>('/auth/industries'),
  getTree: () =>
    api.get<{ success: boolean; data: { id: number; name: string; children: { id: number; name: string }[] }[] }>('/auth/industries/tree'),
};

export const configApi = {
  getPresets: () =>
    api.get('/config/presets'),

  getScenePreset: (sceneId: string) =>
    api.get(`/config/presets/scene/${sceneId}`),

  getUserConfig: () =>
    api.get('/config/user'),

  updateUserConfig: (data: Record<string, unknown>) =>
    api.put('/config/user', data),

  initialize: (profile: Record<string, unknown>, overrides: Record<string, unknown>) =>
    api.post('/config/user/initialize', { profile, overrides }),

  isInitialized: () =>
    api.get('/config/user/initialized'),

  updateSceneOverride: (sceneId: string, overrides: Record<string, unknown>) =>
    api.put(`/config/user/overrides/${sceneId}`, overrides),

  deleteSceneOverride: (sceneId: string) =>
    api.delete(`/config/user/overrides/${sceneId}`),
};

export const projectsApi = {
  list: (status = 'active') =>
    api.get<{ projects: Project[] }>('/projects', { params: { status } }),

  create: (data: { name: string; description?: string; sceneId?: string }) =>
    api.post<Project>('/projects', data),

  get: (id: number) =>
    api.get<Project & { images: ProjectImage[] }>(`/projects/${id}`),

  update: (id: number, data: Record<string, any>) =>
    api.put(`/projects/${id}`, data),

  delete: (id: number) =>
    api.delete(`/projects/${id}`),

  addImage: (projectId: number, data: { imageBase64: string; prompt: string; sceneId?: string; params?: any; changeNote?: string }) =>
    api.post<{ id: number; filePath: string; fileSize: number; version: number }>(`/projects/${projectId}/images`, data),

  updateImage: (projectId: number, imageId: number, data: { imageBase64: string; prompt?: string; params?: any; changeNote?: string }) =>
    api.put<{ id: number; filePath: string; fileSize: number; version: number }>(`/projects/${projectId}/images/${imageId}`, data),

  getVersions: (projectId: number, imageId: number) =>
    api.get<{ versions: ImageVersion[] }>(`/projects/${projectId}/images/${imageId}/versions`),

  deleteImage: (projectId: number, imageId: number) =>
    api.delete(`/projects/${projectId}/images/${imageId}`),

  favoriteImage: (projectId: number, imageId: number) =>
    api.post(`/projects/${projectId}/images/${imageId}/favorite`),

  unfavoriteImage: (projectId: number, imageId: number) =>
    api.delete(`/projects/${projectId}/images/${imageId}/favorite`),
};

export const creditsApi = {
  getBalance: () =>
    api.get<{ balance: number }>('/credits/balance'),

  getTransactions: (page = 1, pageSize = 20, type?: string) =>
    api.get<{ items: any[]; total: number }>('/credits/transactions', { params: { page, pageSize, type } }),

  getPackages: () =>
    api.get<{ packages: any[]; creditRules: any }>('/credits/packages'),

  recharge: (packageId: string) =>
    api.post<{ success: boolean; balance: number; added: number }>('/credits/recharge', { packageId }),
};

export const conversationsApi = {
  list: () =>
    api.get<{ conversations: Conversation[] }>('/conversations'),

  create: (data: { sceneId?: string; phaseId?: string; chartId?: string; title?: string }) =>
    api.post<Conversation>('/conversations', data),

  get: (id: number) =>
    api.get<{ conversation: Conversation; messages: ChatMessage[] }>(`/conversations/${id}`),

  update: (id: number, data: { title: string }) =>
    api.put(`/conversations/${id}`, data),

  delete: (id: number) =>
    api.delete(`/conversations/${id}`),

  sendMessage: (conversationId: number, data: { role: 'user' | 'assistant' | 'system'; content?: string; sceneId?: string; imageId?: number; imageUrl?: string; prompt?: string; parentMessageId?: number; metadata?: any }) =>
    api.post<ChatMessage>(`/conversations/${conversationId}/messages`, data),

  saveImage: (conversationId: number, data: { imageBase64?: string; imageUrl?: string; prompt: string; sceneId?: string; qualityTier?: string; contextSummary?: string; metadata?: { suggestions?: string[] } }) =>
    api.post<{ imageId: number; filePath: string; fileUrl: string; fileSize: number; version: number; projectId: number; previewUrl?: string; qualityTier?: string }>(`/conversations/${conversationId}/save-image`, data),

  generateTitle: (conversationId: number) =>
    api.post<{ title: string }>(`/conversations/${conversationId}/generate-title`),

  lockScene: (conversationId: number, sceneId: string) =>
    api.put(`/conversations/${conversationId}/lock-scene`, { sceneId }),

  getContext: (conversationId: number) =>
    api.get<{ contextSummary: string; sceneId?: string; isSceneLocked: boolean; messageCount: number }>(`/conversations/${conversationId}/context`),

  updateReaction: (conversationId: number, messageId: number, type: 'like' | 'dislike' | 'clear') =>
    api.put(`/conversations/${conversationId}/messages/${messageId}/reaction`, { type }),

  regenerateMessage: (conversationId: number, messageId: number) =>
    api.post<{ prompt: string; sceneId?: string }>(`/conversations/${conversationId}/messages/${messageId}/regenerate`),

  updateMessageMetadata: (conversationId: number, messageId: number, metadata: Record<string, unknown>) =>
    api.patch<{ success: boolean; metadata: Record<string, unknown> }>(`/conversations/${conversationId}/messages/${messageId}/metadata`, { metadata }),
};

export const filesApi = {
  list: (params?: { type?: string; scene?: string; search?: string; page?: number; limit?: number }) =>
    api.get<{ files: any[]; total: number; page: number; limit: number }>('/files', { params }),

  upload: (data: { fileName: string; fileType: 'reference' | 'document'; data: string; mimeType: string; sceneId?: string; conversationId?: number }) =>
    api.post<{ id: number; filePath: string; fileUrl: string; fileSize: number; fileName: string }>('/files/upload', data),

  saveGenerated: (data: { imageBase64: string; prompt: string; sceneId?: string; conversationId?: number; imageId?: number; qualityTier?: string; creditCost?: number }) =>
    api.post<{ id: number; filePath: string; fileUrl: string; fileSize: number }>('/files/save-generated', data),

  delete: (id: number) =>
    api.delete(`/files/${id}`),

  stats: () =>
    api.get<{ stats: Record<string, { count: number; totalSize: number }>; totalCount: number; totalSize: number }>('/files/stats'),
};

export const scenesApi = {
  getTree: () =>
    api.get<{ tree: any[] }>('/scenes/tree'),

  getPhases: () =>
    api.get<{ phases: any[] }>('/scenes/phases'),

  getGroups: (phaseId?: string) =>
    api.get<{ groups: any[] }>('/scenes/groups', { params: phaseId ? { phaseId } : {} }),

  getCharts: (groupId?: string) =>
    api.get<{ charts: any[] }>('/scenes/charts', { params: groupId ? { groupId } : {} }),

  getChart: (id: string) =>
    api.get<any>(`/scenes/charts/${id}`),

  useChart: (id: string) =>
    api.post(`/scenes/charts/${id}/use`),
};

export default api;
